import type Matter from 'matter-js';
import { type Container, Graphics } from 'pixi.js';
import { BOUNDARY_DISTANCE } from '@/canvas/consts/layout';
import type { PhysicsEngine } from '@/canvas/systems/PhysicsEngine';

interface BoundaryCollisionCallbackParams {
  isGameEnded: () => boolean;
  isPendingGameEnd: () => boolean;
  boundaryBoxes: NonNullable<BoundaryBoxes>;
  setPendingGameEnd: (value: boolean) => void;
}

type BoundaryBoxes = ReturnType<PhysicsEngine['getBoundaryBoxes']>;

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const getBoundaryBoxRects = (
  screenWidth: number,
  screenHeight: number,
  boundaryThickness: number,
  boundaryDistance: number = BOUNDARY_DISTANCE,
): Rect[] => {
  const topWidth = screenWidth * 3;
  const topCenterX = screenWidth / 2;
  const topCenterY = -boundaryDistance - boundaryThickness / 2;
  const topLeftX = topCenterX - topWidth / 2;
  const topLeftY = topCenterY - boundaryThickness / 2;

  const bottomLeftY = screenHeight + boundaryDistance;

  const leftHeight = screenHeight * 3;
  const leftCenterX = -boundaryDistance - boundaryThickness / 2;
  const leftCenterY = screenHeight / 2;
  const leftLeftX = leftCenterX - boundaryThickness / 2;
  const leftTopY = leftCenterY - leftHeight / 2;

  const rightCenterX = screenWidth + boundaryDistance + boundaryThickness / 2;
  const rightLeftX = rightCenterX - boundaryThickness / 2;

  return [
    { x: topLeftX, y: topLeftY, width: topWidth, height: boundaryThickness },
    { x: topLeftX, y: bottomLeftY, width: topWidth, height: boundaryThickness },
    { x: leftLeftX, y: leftTopY, width: boundaryThickness, height: leftHeight },
    {
      x: rightLeftX,
      y: leftTopY,
      width: boundaryThickness,
      height: leftHeight,
    },
  ];
};

export const drawBoundaryBoxes = (
  container: Container,
  screenWidth: number,
  screenHeight: number,
  boundaryThickness: number,
): void => {
  const boundaryBoxes = new Graphics();
  const rects = getBoundaryBoxRects(
    screenWidth,
    screenHeight,
    boundaryThickness,
  );

  for (const rect of rects) {
    boundaryBoxes.rect(rect.x, rect.y, rect.width, rect.height);
  }
  boundaryBoxes.fill({ color: 0xff0000, alpha: 0.5 });

  container.addChild(boundaryBoxes);
};

/**
 * 境界ボックスとの衝突を検知し、衝突時に pendingGameEnd フラグを立てるコールバックを生成する。
 * collisionStart 内でゲーム終了処理を直接実行すると Matter.js Resolver が
 * 不整合状態になりクラッシュするため、フラグのみ立て update() で処理する。
 */
export const createBoundaryCollisionCallback = ({
  isGameEnded,
  isPendingGameEnd,
  boundaryBoxes,
  setPendingGameEnd,
}: BoundaryCollisionCallbackParams) => {
  return (event: Matter.IEventCollision<Matter.Engine>): void => {
    if (isGameEnded() || isPendingGameEnd()) return;

    const hasBoundaryCollision = event.pairs.some((pair) =>
      isBoundaryCollisionPair(pair, boundaryBoxes),
    );
    if (hasBoundaryCollision) {
      setPendingGameEnd(true);
    }
  };
};

export const isBoundaryCollisionPair = (
  pair: Matter.Pair,
  boundaryBoxes: NonNullable<BoundaryBoxes>,
): boolean => {
  const bodyA = pair.bodyA;
  const bodyB = pair.bodyB;
  const isBoundaryA =
    bodyA === boundaryBoxes.top ||
    bodyA === boundaryBoxes.bottom ||
    bodyA === boundaryBoxes.left ||
    bodyA === boundaryBoxes.right;
  const isBoundaryB =
    bodyB === boundaryBoxes.top ||
    bodyB === boundaryBoxes.bottom ||
    bodyB === boundaryBoxes.left ||
    bodyB === boundaryBoxes.right;

  return (
    (isBoundaryA && bodyB.label?.startsWith('block_')) ||
    (isBoundaryB && bodyA.label?.startsWith('block_'))
  );
};
